import axios from "axios";

// ---------------------------------------------------------------------------
// Base URL resolution
//
// Single source of truth for the API base URL.
//   1. REACT_APP_API_URL from process.env if present (CRA injects at build).
//   2. Fall back to window.__API_URL__ if a host page injects it at runtime.
//   3. Last resort: same-origin "/api/" — keeps the app from posting to
//      "undefined/Auth/login" when the .env file is missing in a build.
//
// helpers/api_helper.js routes through THIS instance, so there is no
// second base URL anywhere in the app.
// ---------------------------------------------------------------------------
// const resolvedBaseURL =
//     (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
//     (typeof window !== "undefined" && window.__API_URL__) ||
//     "https://localhost:7168/api/";



// Owner incident (2026-08-03) — this fallback silently sent EVERY request
// from local development to the LIVE PRODUCTION backend, which is why days
// of local fixes "didn't work": localhost was testing production's old code
// and writing test EPOs into the production database. The production URL is
// now reachable ONLY when the page is actually served from a non-local host;
// on localhost the fallback is the CRA proxy ("/api/" → package.json proxy →
// https://localhost:7168). REACT_APP_API_URL still wins when set — but note
// CRA reads .env ONLY at `npm start` launch, so restart after editing it.
//
// The non-local fallback is the Azure API App Service over HTTPS. It must
// stay https:// — the frontend is served over HTTPS, so an http:// value here
// is blocked by the browser as mixed content (symptom: "requested an insecure
// XMLHttpRequest endpoint ... has been blocked"). This fallback should only
// ever be reached if REACT_APP_API_URL is missing from the build; the value
// in .env.production is the intended source.
const isLocalHost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname);
const resolvedBaseURL =
    (typeof process !== "undefined" && process.env && process.env.REACT_APP_API_URL) ||
    (typeof window !== "undefined" && window.__API_URL__) ||
    (isLocalHost
        ? "/api/"
        : "https://sswdashboard-prod-cus-api.azurewebsites.net/api/");

console.log("API base URL:", resolvedBaseURL);
const api = axios.create({
    baseURL: resolvedBaseURL,
    headers: { "Content-Type": "application/json" },
});

// 🔐 Attach JWT + EmpId/Dept/Role on EVERY request (read fresh from
// sessionStorage each time — never cached at module load).
api.interceptors.request.use((config) => {
    try {
        const authData = sessionStorage.getItem("authUser");
        if (authData) {
            const parsed = JSON.parse(authData);

            const token = parsed?.token || parsed?.data?.token;
            if (token) config.headers.Authorization = `Bearer ${token}`;

            const empId = parsed?.uid || parsed?.data?.uid;
            if (empId) config.headers["X-EmpId"] = empId;

            const dept = parsed?.department || parsed?.data?.department;
            if (dept) config.headers["X-Dept"] = dept;

            const role = parsed?.role || parsed?.data?.role;
            if (role) config.headers["X-Role"] = role;

            // Merged from development branch — surface MPC flag for legacy parity
            const isMPC = parsed?.isMPC || parsed?.data?.isMPC;
            if (isMPC) config.headers["X-IsMPC"] = isMPC;
        }

        // When the body is FormData, NEVER let our JSON default leak through.
        // Axios+browser will set the correct 'multipart/form-data; boundary=...'
        // automatically as long as no Content-Type is present.
        // This protects Contract Review attachment uploads (and any future
        // multipart caller — EPO attachments, DMS attachments, etc.).
        if (typeof FormData !== "undefined" && config.data instanceof FormData) {
            if (config.headers && typeof config.headers.delete === "function") {
                config.headers.delete("Content-Type");
            } else if (config.headers) {
                delete config.headers["Content-Type"];
                delete config.headers["content-type"];
            }
        }
    } catch (err) {
        // Defensive: malformed sessionStorage shouldn't break the request.
        // Original logged here; preserved for parity.
        console.error("Error parsing authUser:", err);
    }
    return config;
});

// Exported so helpers/api_helper.js can route through the same instance.
// New code should still default to `import api from "../api"`.
export { api };
export default api;
